'use client'

import { useSettingsStore } from './store'
import { useAuth } from '@/components/auth'
import type { PremiumFeature } from './types'
import { isPenthouse, canUseFeature, maxRoostrs } from './subscription-constants'

// Re-export everything from constants so existing imports keep working
export {
  MAX_ROOSTRS_FREE,
  MAX_ROOSTRS_PENTHOUSE,
  isPenthouse,
  canUseFeature,
  maxRoostrs,
  PREMIUM_FEATURE_LABELS,
  ALL_PREMIUM_FEATURES,
} from './subscription-constants'

/** React hook that exposes subscription state from the settings store. */
export function useSubscription() {
  const { subscription } = useSettingsStore()
  const { user, loading } = useAuth()

  return {
    subscription,
    plan: subscription.plan,
    isPenthouse: isPenthouse(subscription),
    isLoading: loading,
    isAuthenticated: !!user,
    canUse: (feature: PremiumFeature) => canUseFeature(subscription, feature),
    maxRoostrs: maxRoostrs(subscription),
    currentPeriodEnd: subscription.currentPeriodEnd,
    cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
  }
}

/**
 * Redirect the user to Stripe Checkout. Calls the backend to create a session.
 * Returns the checkout URL or null if the API is not yet configured.
 */
export async function redirectToCheckout(interval: string = 'monthly'): Promise<string | null> {
  try {
    console.debug('[subscription] Creating checkout session...', { interval })
    const { api } = await import('./api')
    const response = await api.createCheckoutSession(interval)
    console.debug('[subscription] Checkout response:', response)
    if (response?.url) {
      console.debug('[subscription] Redirecting to:', response.url)
      window.location.href = response.url
      return response.url
    }
    console.warn('[subscription] No URL in checkout response')
    return null
  } catch (error) {
    console.error('[subscription] Failed to create checkout session:', error)
    throw error
  }
}

/**
 * Redirect the user to the Stripe Customer Portal for subscription management.
 */
export async function redirectToPortal(): Promise<string | null> {
  try {
    console.debug('[subscription] Creating portal session...')
    const { api } = await import('./api')
    const response = await api.createPortalSession()
    console.debug('[subscription] Portal response:', response)
    if (response?.url) {
      console.debug('[subscription] Redirecting to:', response.url)
      window.location.href = response.url
      return response.url
    }
    console.warn('[subscription] No URL in portal response')
    return null
  } catch (error) {
    console.error('[subscription] Failed to create portal session:', error)
    throw error
  }
}
